import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getHostedTokenByHash,
  checkoutAuthorizeNet,
  updateCardAuthorizeNet,
} from 'services/authorizeNet/authorizeNet';
import { AuthnetMessagePayload, AuthnetPayerBody } from 'services/authorizeNet/types';
import { APPROVED_RESPONSE_CODE, parseAuthnetMessage } from './useAuthorizeNetHandler';

export type AuthnetLinkIntent = 'checkout' | 'updateCard';

interface UseAuthorizeNetLinkHandlerOptions {
  hash: string;
  region: string;
  applicationId: string;
  onError: (type: string) => void;
  onSuccess: () => void;
}

interface StartParams {
  intent: AuthnetLinkIntent;
  ids: number[];
  payer: AuthnetPayerBody;
}

export function useAuthorizeNetLinkHandler({
  hash,
  region,
  applicationId,
  onError,
  onSuccess,
}: UseAuthorizeNetLinkHandlerOptions) {
  const [formToken, setFormToken] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const pendingRef = useRef<StartParams | null>(null);

  const handleResult = useCallback(
    async (payload: AuthnetMessagePayload) => {
      if (payload.action === 'cancel') {
        setFormToken('');
        setIsLoaded(false);
        return;
      }

      if (payload.action !== 'transactResponse') {
        return;
      }

      const pending = pendingRef.current;

      if (payload.responseCode !== APPROVED_RESPONSE_CODE || !payload.transId || !pending) {
        setFormToken('');
        setIsLoaded(false);
        onError('payment');
        return;
      }

      setFormToken('');
      setIsLoaded(true);
      const res =
        pending.intent === 'checkout'
          ? await checkoutAuthorizeNet(payload.transId, pending.ids, pending.payer, region, hash, applicationId)
          : await updateCardAuthorizeNet(payload.transId, hash, pending.payer, region);
      setIsLoaded(false);
      pendingRef.current = null;

      if (!res.success) {
        onError('payment');
        return;
      }

      onSuccess();
    },
    [hash, region, applicationId, onError, onSuccess],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const payload = parseAuthnetMessage(event.data);
      if (payload) {
        handleResult(payload);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleResult]);

  const startAuthorizeNetLink = useCallback(
    async ({ intent, ids, payer }: StartParams) => {
      pendingRef.current = { intent, ids, payer };
      setIsLoaded(true);
      const res = await getHostedTokenByHash(hash, ids, region, intent === 'updateCard');
      setIsLoaded(false);

      if (!res.success) {
        pendingRef.current = null;
        onError('payment');
        return;
      }

      setFormToken(res.data.formToken);
    },
    [hash, region, onError],
  );

  const closeAuthorizeNetLink = useCallback(() => {
    setFormToken('');
    setIsLoaded(false);
    pendingRef.current = null;
  }, []);

  return { formToken, isLoaded, startAuthorizeNetLink, closeAuthorizeNetLink };
}
